/**
 * Chat domain logic (pure) + persistence.
 *
 * Chats are simulated 1:1 conversations with video authors (NSFW mode).
 * The "author" is driven by the scripted conversation engine (`chatScript`):
 * every user message advances the storyline by one beat and schedules a
 * ragged-delay author reply (which may carry a photo or reveal the call
 * button). Pending replies are persisted with a `deliverAt` timestamp so they
 * survive reloads / navigation (no reliance on a live timer being alive).
 *
 * @typedef {Object} ChatMedia
 * @property {'photo'|'video'} kind
 * @property {string} [url]      - direct URL (author template media)
 * @property {string} [blobId]   - mediaStore reference (user uploads)
 *
 * @typedef {Object} ChatMessage
 * @property {string} id
 * @property {'me'|'author'} from
 * @property {string} text
 * @property {number} at
 * @property {?ChatMedia} [media]
 * @property {'sent'|'read'} [status]  - delivery state of own messages
 * @property {boolean} [edited]
 * @property {'call'} [kind]           - special system-style call row
 *
 * @typedef {Object} Chat
 * @property {string} id           - stable per author+channel
 * @property {string} authorId
 * @property {string} authorName
 * @property {string} letter       - avatar letter (A-Z)
 * @property {string} channel
 * @property {number} createdAt
 * @property {number} lastAt
 * @property {number} replyCount   - author replies delivered so far
 * @property {number} unread        - undelivered-to-user author replies
 * @property {number} step          - current scripted beat index
 * @property {Object} flags         - accumulated keyword intent flags
 * @property {boolean} callOffered  - author has revealed the call button
 * @property {ChatMessage[]} messages
 * @property {?{ text: string, media: ?ChatMedia, offersCall: boolean, deliverAt: number }} pending
 */
import { getJSON, setJSON, getItem, setItem } from './storage.js';
import { advanceConversation } from './chatScript.js';

const CHATS_KEY = 'authorChats';
const REWARDED_CHECK_KEY = '_lastRewardedCheckAt';

// A reply unfolds in two human-like phases:
//   1) NOTICE/READ+THINK: time before the author even "sees" the message and
//      starts typing. Usually a few seconds, but sometimes she's distracted and
//      only notices after 15-60s. The typing dots are HIDDEN during this phase.
//   2) TYPING: how long the "…" indicator shows before the reply lands.
export const NOTICE_QUICK_MIN_MS = 1500;
export const NOTICE_QUICK_MAX_MS = 8500;
export const NOTICE_SLOW_MIN_MS = 15000;
export const NOTICE_SLOW_MAX_MS = 60000;
export const NOTICE_SLOW_CHANCE = 0.3; // chance she's "distracted"
// The "…" duration scales with the length of the reply (realistic phone
// typing), so a short line types fast and a long one takes longer. The dots
// shown and the message that lands now match.
export const TYPING_MS_PER_CHAR = 55; // ~ a quick texter (~36 wpm)
export const TYPING_BASE_MS = 600; // think/lock-in before the first key
export const TYPING_MIN_MS = 1200;
export const TYPING_MAX_MS = 16000;
export const REWARDED_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes

/** Chance the author sends a follow-up text after a call ends. */
export const CALL_FOLLOWUP_CHANCE = 0.3;

/** Short lines the author might send right after a call ends. */
export const CALL_FOLLOWUP_REPLIES = Object.freeze([
  'Mmm that was fun, we should do that again soon',
  'I loved hearing your voice, call me again later?',
  'You hung up too soon, I was not done with you',
  'That made my whole night, text me okay?',
  'Okay now I really cannot stop thinking about you',
]);

const rand = (min, max) => min + Math.floor(Math.random() * (max - min));
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/** How long the "…" should show for a reply of this length (with jitter). */
function typingTimeFor(text) {
  const len = String(text || '').length;
  const base = TYPING_BASE_MS + len * TYPING_MS_PER_CHAR;
  const jitter = 0.85 + Math.random() * 0.3; // ±15%
  return clamp(Math.round(base * jitter), TYPING_MIN_MS, TYPING_MAX_MS);
}

/**
 * Build the two-phase reply schedule from `now`:
 *  - typingStartAt: when the author notices and starts typing (dots appear).
 *  - deliverAt:     when the reply actually lands (typing time scales with text).
 */
function scheduleReply(now, text) {
  const distracted = Math.random() < NOTICE_SLOW_CHANCE;
  const notice = distracted
    ? rand(NOTICE_SLOW_MIN_MS, NOTICE_SLOW_MAX_MS)
    : rand(NOTICE_QUICK_MIN_MS, NOTICE_QUICK_MAX_MS);
  const typingStartAt = now + notice;
  return { typingStartAt, deliverAt: typingStartAt + typingTimeFor(text) };
}

const uid = () => `${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
const randomLetter = () =>
  String.fromCharCode(65 + Math.floor(Math.random() * 26));

/** @returns {Chat[]} */
export function loadChats() {
  const list = getJSON(CHATS_KEY, []);
  return Array.isArray(list) ? list : [];
}

export function saveChats(list) {
  setJSON(CHATS_KEY, list);
}

export function findChat(list, authorId, channel) {
  return list.find((c) => c.authorId === authorId && c.channel === channel);
}

/** Stable chat id from author + channel. */
export function chatIdFor(authorId, channel) {
  return `${channel}:${authorId}`;
}

/**
 * Ensure a chat exists for the given author/channel. Returns the updated list
 * and the chat. Idempotent: re-opening an existing author returns it untouched.
 */
export function openChat(list, { authorId, authorName, channel }) {
  const existing = findChat(list, authorId, channel);
  if (existing) return { list, chat: existing };
  const now = Date.now();
  const chat = {
    id: chatIdFor(authorId, channel),
    authorId,
    authorName: authorName || `Author ${randomLetter()}`,
    letter: (authorName || randomLetter())[0].toUpperCase(),
    channel,
    createdAt: now,
    lastAt: now,
    replyCount: 0,
    unread: 0,
    step: 0,
    flags: {},
    affection: 0,
    photoRequests: 0,
    photosSent: 0,
    lastLine: '',
    callOffered: false,
    messages: [],
    pending: null,
  };
  return { list: [chat, ...list], chat };
}

/**
 * Append a user message (text and/or media) and schedule the author's reply
 * via the scripted conversation engine.
 *
 * @param {Chat[]} list
 * @param {string} chatId
 * @param {{ text?: string, media?: ChatMedia }} payload
 */
export function addUserMessage(list, chatId, payload) {
  const { text = '', media = null } = payload || {};
  const now = Date.now();
  return list.map((c) => {
    if (c.id !== chatId) return c;
    const messages = [
      ...c.messages,
      { id: uid(), from: 'me', text, media, at: now, status: 'sent' },
    ];
    // Decide the author's next action now; deliver it after a ragged delay.
    const action = advanceConversation(c, { text, media });
    // If she was already typing, she stays in the typing phase and replies fast.
    const wasTyping = !!c.pending?.typing;
    const schedule = wasTyping
      ? { typing: true, typingStartAt: now, deliverAt: now + typingTimeFor(action.text) }
      : { typing: false, ...scheduleReply(now, action.text) };
    return {
      ...c,
      messages,
      lastAt: now,
      step: action.step,
      flags: action.flags,
      affection: action.affection,
      photoRequests: action.photoRequests,
      photosSent: action.photosSent,
      lastLine: action.lastLine,
      callOffered: c.callOffered || action.offersCall,
      pending: {
        text: action.text,
        media: action.media || null,
        offersCall: action.offersCall,
        ...schedule,
      },
    };
  });
}

/** Edit the text of an own message in place (marks it edited). */
export function editMessage(list, chatId, msgId, text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return list;
  return list.map((c) => {
    if (c.id !== chatId) return c;
    return {
      ...c,
      messages: c.messages.map((m) =>
        m.id === msgId && m.from === 'me'
          ? { ...m, text: trimmed, edited: true }
          : m,
      ),
    };
  });
}

/**
 * Record an ended call as a chat row and (with CALL_FOLLOWUP_CHANCE) schedule a
 * short author follow-up message.
 */
export function addCallEnded(list, chatId, durationSec) {
  const now = Date.now();
  return list.map((c) => {
    if (c.id !== chatId) return c;
    const messages = [
      ...c.messages,
      {
        id: uid(),
        from: 'me',
        kind: 'call',
        text: `Call \u00b7 ${formatDuration(durationSec)}`,
        at: now,
        status: 'read',
      },
    ];
    const followUp = Math.random() < CALL_FOLLOWUP_CHANCE;
    const followUpText =
      CALL_FOLLOWUP_REPLIES[Math.floor(Math.random() * CALL_FOLLOWUP_REPLIES.length)];
    return {
      ...c,
      messages,
      lastAt: now,
      pending: followUp
        ? {
            text: followUpText,
            media: null,
            offersCall: false,
            typing: false,
            ...scheduleReply(now, followUpText),
          }
        : c.pending,
    };
  });
}

function formatDuration(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** Clear the unread counter for a chat (called when the user opens it). */
export function markRead(list, chatId) {
  return list.map((c) => (c.id === chatId && c.unread ? { ...c, unread: 0 } : c));
}

/** Total undelivered-to-user author replies across all chats. */
export function totalUnread(list) {
  return list.reduce((sum, c) => sum + (c.unread || 0), 0);
}

/**
 * Flip pending replies into the "typing" phase once their typingStartAt passes.
 * Starting to type also marks the user's prior messages as read (she saw them).
 * Legacy pending without a typingStartAt is treated as already typing.
 * @returns {{ list: Chat[], changed: boolean, nextTypingAt: number|null }}
 */
export function startDueTyping(list, now = Date.now()) {
  let changed = false;
  let nextTypingAt = null;
  const next = list.map((c) => {
    const p = c.pending;
    if (!p || p.typing) return c;
    const startAt = p.typingStartAt ?? now;
    if (startAt <= now) {
      changed = true;
      const seen = c.messages.map((m) =>
        m.from === 'me' && m.status !== 'read' ? { ...m, status: 'read' } : m,
      );
      return { ...c, messages: seen, pending: { ...p, typing: true } };
    }
    if (nextTypingAt == null || startAt < nextTypingAt) nextTypingAt = startAt;
    return c;
  });
  return { list: next, changed, nextTypingAt };
}

/**
 * Advance all chats by one tick: start due typing, then deliver due replies.
 * Returns the next timestamp the caller should wake up for (typing or deliver).
 * @returns {{ list: Chat[], changed: boolean, nextAt: number|null }}
 */
export function tickChats(list, now = Date.now()) {
  const a = startDueTyping(list, now);
  const b = deliverDuePending(a.list, now);
  const candidates = [a.nextTypingAt, b.nextDeliverAt].filter((x) => x != null);
  return {
    list: b.list,
    changed: a.changed || b.changed,
    nextAt: candidates.length ? Math.min(...candidates) : null,
  };
}

/**
 * Deliver every pending author reply whose time has come. Delivering a reply
 * also marks the user's prior messages as read (the author "saw" them).
 * @returns {{ list: Chat[], changed: boolean, nextDeliverAt: number|null }}
 */
export function deliverDuePending(list, now = Date.now()) {
  let changed = false;
  let nextDeliverAt = null;
  const next = list.map((c) => {
    if (!c.pending) return c;
    if (c.pending.deliverAt <= now) {
      changed = true;
      const seen = c.messages.map((m) =>
        m.from === 'me' && m.status !== 'read' ? { ...m, status: 'read' } : m,
      );
      return {
        ...c,
        messages: [
          ...seen,
          {
            id: uid(),
            from: 'author',
            text: c.pending.text,
            media: c.pending.media || null,
            at: now,
          },
        ],
        lastAt: now,
        replyCount: c.replyCount + 1,
        unread: (c.unread || 0) + 1,
        callOffered: c.callOffered || c.pending.offersCall,
        pending: null,
      };
    }
    if (nextDeliverAt == null || c.pending.deliverAt < nextDeliverAt) {
      nextDeliverAt = c.pending.deliverAt;
    }
    return c;
  });
  return { list: next, changed, nextDeliverAt };
}

/* ------------------------------------------------------------------ *
 * Rewarded-ad rate limit (max once per 10 minutes)
 * ------------------------------------------------------------------ */

/** True if we're allowed to run the rewarded check now (and stamps it). */
export function consumeRewardedCheck(now = Date.now()) {
  const last = parseInt(getItem(REWARDED_CHECK_KEY) || '0', 10) || 0;
  if (now - last < REWARDED_COOLDOWN_MS) return false;
  setItem(REWARDED_CHECK_KEY, String(now));
  return true;
}
